import React, { useCallback } from "react";
import { styled } from "styled-components";
import { Trans, useTranslation } from "react-i18next";
import { ProgressAnimation, Icon, Spinner, HoverForHelp } from "components/common/index";
import { Button, DropdownButton, MenuItem, ButtonGroup } from "components/bootstrap";
import ReadableDuration from "components/common/ReadableDuration";
import useAutoRefresh from "views/hooks/useAutoRefresh";
import { durationToMS } from "util/DateTime";
const FlexibleButtonGroup = styled(ButtonGroup)`
  display: flex;
  justify-content: flex-end;
  position: relative;

  > .btn-group {
    .btn:first-child {
      max-width: 100%;
    }
  }
`;
const ButtonLabel = ({ children }) => {
  const { t } = useTranslation("common");
  const { refreshConfig } = useAutoRefresh();
  if (!(refreshConfig == null ? void 0 : refreshConfig.enabled)) {
    return /* @__PURE__ */ React.createElement(React.Fragment, null, t("refresh.not_updating"), children);
  }
  return /* @__PURE__ */ React.createElement(React.Fragment, null, t("refresh.every"), " ", /* @__PURE__ */ React.createElement(ReadableDuration, { duration: refreshConfig.interval }), children);
};
const RefreshControls = ({
  children = null,
  humanName,
  disable,
  onToggle = null,
  onEnable = null,
  onDisable = null,
  intervalOptions,
  onSelectInterval = null,
  isLoadingMinimumInterval,
  minimumRefreshInterval,
  defaultInterval
}) => {
  const { t } = useTranslation("common");
  const { refreshConfig, startAutoRefresh, stopAutoRefresh, animationId } = useAutoRefresh();
  const selectInterval = useCallback(
    (interval) => {
      startAutoRefresh(durationToMS(interval));
      if (typeof onSelectInterval === "function") {
        onSelectInterval(interval);
      }
    },
    [onSelectInterval, startAutoRefresh]
  );
  const toggleEnable = useCallback(() => {
    var _a;
    if (!defaultInterval && !(refreshConfig == null ? void 0 : refreshConfig.interval)) {
      return;
    }
    if (typeof onToggle === "function") {
      onToggle(!(refreshConfig == null ? void 0 : refreshConfig.enabled));
    }
    if (refreshConfig == null ? void 0 : refreshConfig.enabled) {
      stopAutoRefresh();
      if (typeof onDisable === "function") {
        onDisable();
      }
    } else {
      startAutoRefresh((_a = refreshConfig == null ? void 0 : refreshConfig.interval) != null ? _a : durationToMS(defaultInterval));
      if (typeof onEnable === "function") {
        onEnable();
      }
    }
  }, [
    defaultInterval,
    onDisable,
    onEnable,
    onToggle,
    refreshConfig == null ? void 0 : refreshConfig.enabled,
    refreshConfig == null ? void 0 : refreshConfig.interval,
    startAutoRefresh,
    stopAutoRefresh
  ]);
  return /* @__PURE__ */ React.createElement(FlexibleButtonGroup, { "aria-label": t("refresh_controls.controls_label", { humanName }) }, (refreshConfig == null ? void 0 : refreshConfig.enabled) && animationId && /* @__PURE__ */ React.createElement(
    ProgressAnimation,
    {
      key: `${refreshConfig.interval}-${animationId}`,
      $animationDuration: refreshConfig.interval,
      $increase: false
    }
  ), /* @__PURE__ */ React.createElement(
    Button,
    {
      onClick: toggleEnable,
      title: (refreshConfig == null ? void 0 : refreshConfig.enabled) ? t("refresh.pause") : t("refresh.start"),
      disabled: disable || isLoadingMinimumInterval || !defaultInterval
    },
    /* @__PURE__ */ React.createElement(Icon, { name: (refreshConfig == null ? void 0 : refreshConfig.enabled) ? "pause" : "update" })
  ), /* @__PURE__ */ React.createElement(DropdownButton, { title: /* @__PURE__ */ React.createElement(ButtonLabel, null, children), disabled: disable, id: "refresh-options-dropdown" }, isLoadingMinimumInterval && /* @__PURE__ */ React.createElement(Spinner, null), !isLoadingMinimumInterval && intervalOptions.map(([interval, label]) => {
    const isBelowMinimum = durationToMS(interval) < durationToMS(minimumRefreshInterval);
    return /* @__PURE__ */ React.createElement(
      MenuItem,
      {
        key: `RefreshControls-${label}`,
        onClick: () => selectInterval(interval),
        disabled: isBelowMinimum
      },
      label,
      isBelowMinimum && /* @__PURE__ */ React.createElement(HoverForHelp, { displayLeftMargin: true }, /* @__PURE__ */ React.createElement(
        Trans,
        {
          t,
          i18nKey: "refresh_controls.below_minimum",
          values: { interval, minimum: minimumRefreshInterval },
          components: {
            intervalDuration: /* @__PURE__ */ React.createElement(ReadableDuration, { duration: interval }),
            minimumDuration: /* @__PURE__ */ React.createElement(ReadableDuration, { duration: minimumRefreshInterval })
          }
        }
      ))
    );
  })));
};
export default RefreshControls;
